import { Component, OnInit, OnDestroy, ComponentRef } from '@angular/core';
import { Sprint } from '../../../models/sprint';
import { Project } from '../../../models/project';
import { ProjectService } from '../../../services/project.service';
import { SprintService } from '../../../services/sprint.service';
import { SettingsService } from '../../../services/settings.service';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { Subscription } from 'rxjs';
import { add, formatISO } from 'date-fns';
import { IWidget } from '../../../interfaces/iWidget';

@Component({
  selector: 'app-sprintplanning-widget',
  templateUrl: './sprintplanning-widget.component.html',
  styleUrls: ['./sprintplanning-widget.component.scss']
  })
export class SprintplanningWidgetComponent implements OnInit, OnDestroy, IWidget {

  static widgetName = 'sprintplanning';
  private subscriptions: Subscription[] = [];
  sprints: Sprint[];
  selectedProjectKey: string;
  selectedSprintId: number;
  public projectId;
  public selectedSprint: Sprint;
  public selectedProject: Project;
  public editModeSprintName = false;


  // callbacks
  public setPosition;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(
    private projectService: ProjectService,
    private sprintService: SprintService,
    private settingsService: SettingsService
  ) {
  }

  ngOnInit() {
    this.selectedSprintId = null;
    this.setCurrentProjectAndSprint();
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  setCurrentProjectAndSprint(): void {
    this.selectedProjectKey = this.settingsService.getProjectName();
    this.projectService.getProjectByKey(this.selectedProjectKey).subscribe((project) => {
      this.projectId = project.id;
      this.loadSprintsForProject(this.selectedProjectKey);
    });
  }


  /**
 * Load all Sprints of a specific Project.
 */
  private loadSprintsForProject(projectKey: string): void {
    this.sprintService.getSprints(projectKey).subscribe(fetchedSprints => {
      this.sprints = fetchedSprints;
      // Reverse so we have to most recently created sprint on top
      this.sprints.reverse();
      this.tryRestoreLastSelectedSprint(projectKey);
      this.selectedSprintId = this.settingsService.getSprintId(this.selectedProjectKey);
      this.selectedSprint = this.sprints.find(x => x.id === this.selectedSprintId);
    });
  }

  /**
   * If there is no sprint selected we try set sprint that was selected last time.
   */
  private tryRestoreLastSelectedSprint(projectKey: string) {
    if (!this.selectedSprintId) {
      const lastUsedSprintId = this.settingsService.getSprintId(projectKey);
      this.selectedSprintId = lastUsedSprintId;
    }
    this.settingsService.emitSettingsUpdate(SettingsEventType.SPRINT_CHANGED, this.constructor.name);
  }


  onSelectedSprintChanged(): void {
    this.loadSprintsForProject(this.selectedProjectKey);
    this.settingsService.setSprintId(this.selectedProjectKey, this.selectedSprintId);
  }

  createSprint(): void {
    const oneWeekInDays = 7;
    const todayAsIsoString = formatISO(new Date(), { representation: 'date' });
    const endDateAsIsoString = formatISO(add(new Date(), { days: oneWeekInDays }), { representation: 'date' });

    if (this.selectedProjectKey) {
      const name: string = this.createNewSprintName();
      const newSprint = new Sprint(null, name, todayAsIsoString, endDateAsIsoString, null);
      this.sprintService.createSprint(this.selectedProjectKey, newSprint).subscribe(createdSprint => {
        this.selectedSprintId = createdSprint.id;
        this.sprintService.getSprintDetails(this.selectedProjectKey, createdSprint.id).subscribe((sprint) => {
          if (sprint !== null) {
            this.selectedSprint = sprint;
            this.settingsService.setSprintId(this.selectedProjectKey, createdSprint.id);
          }
        });
      });
    }
  }

  private createNewSprintName(): string {
    const currentSprintName = this.sprints.find(x => x.id === this.selectedSprintId).name;
    const lastChar = currentSprintName.charAt(currentSprintName.length - 1);
    const secondLastChar = currentSprintName.charAt(currentSprintName.length - 2);
    let newSprintName = '';
    if (!lastChar.match('[0-9]')) {
      newSprintName = currentSprintName + ' 2';
    } else if (secondLastChar.match('[0-9]')) {
      const sprintNumber = Number(secondLastChar + lastChar) + 1;
      newSprintName = currentSprintName.slice(0, length - 2) + sprintNumber;
    } else {
      const sprintNumber = Number(lastChar) + 1;
      newSprintName = currentSprintName.slice(0, length - 1) + sprintNumber;
    }
    return newSprintName;
  }

  changeSprintName(): void {
    this.sprintService.updateSprint(this.selectedProjectKey, this.selectedSprint);
    this.settingsService.emitSettingsUpdate(SettingsEventType.SPRINT_CHANGED, this.constructor.name);
    this.editModeSprintName = !this.editModeSprintName;
  }

  toggleSprintNameEdit(): void {
    this.editModeSprintName = !this.editModeSprintName;
  }

}
